import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Target, TrendingUp, Users, Mail, Phone, BarChart } from 'lucide-react';

const UseCasesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const useCases = [
    {
      icon: Target,
      title: 'Lead-Generierung',
      description: 'Finden Sie gezielt neue Kunden in Ihrer Zielregion und Branche. Keine Zeitverschwendung mehr mit manueller Recherche.',
      image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzY0MzI3MDUwfDA&ixlib=rb-4.1.0&q=85',
      benefits: ['Gezielte Kundenansprache', 'Höhere Conversion-Rate', 'Zeitersparnis']
    },
    {
      icon: TrendingUp,
      title: 'Vertriebsoptimierung',
      description: 'Erweitern Sie Ihr Vertriebsgebiet systematisch. Erschließen Sie neue Märkte mit vollständigen Kontaktdaten.',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzYWxlcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzY0MzI3MDU0fDA&ixlib=rb-4.1.0&q=85',
      benefits: ['Systematische Markterschließung', 'Vollständige Abdeckung', 'Planbare Pipeline']
    },
    {
      icon: Mail,
      title: 'E-Mail-Marketing',
      description: 'Starten Sie zielgerichtete E-Mail-Kampagnen mit bis zu 95% E-Mail-Abdeckung. Direkt in Ihr CRM importierbar.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxkYXRhJTIwZGFzaGJvYXJkfGVufDB8fHx8MTc2NDMyNzA1Mnww&ixlib=rb-4.1.0&q=85',
      benefits: ['95% E-Mail-Abdeckung', 'CRM-Integration', 'Skalierbare Kampagnen']
    },
    {
      icon: Phone,
      title: 'Telefonakquise',
      description: 'Hochwertige Telefonnummern für Ihre Outbound-Strategie. Keine veralteten oder falschen Kontakte mehr.',
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzY0MzI3MDUzfDA&ixlib=rb-4.1.0&q=85',
      benefits: ['Aktuelle Telefonnummern', 'Hohe Erreichbarkeit', 'Qualifizierte Leads']
    },
    {
      icon: Users,
      title: 'Marktanalyse',
      description: 'Analysieren Sie Ihre Zielmärkte und Wettbewerber. Identifizieren Sie Chancen und Potenziale in neuen Regionen.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxkYXRhJTIwZGFzaGJvYXJkfGVufDB8fHx8MTc2NDMyNzA1Mnww&ixlib=rb-4.1.0&q=85',
      benefits: ['Marktpotenzial ermitteln', 'Wettbewerbsanalyse', 'Strategische Planung']
    },
    {
      icon: BarChart,
      title: 'Business Intelligence',
      description: 'Nutzen Sie unsere Daten für tiefe Einblicke in Branchen und Regionen. Treffen Sie datenbasierte Entscheidungen.',
      image: 'https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxkYXRhJTIwZGFzaGJvYXJkfGVufDB8fHx8MTc2NDMyNzA1Mnww&ixlib=rb-4.1.0&q=85',
      benefits: ['Datenbasierte Entscheidungen', 'Branchen-Insights', 'ROI-Optimierung']
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Was Sie mit Adloc alles erreichen können
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Von Lead-Generierung bis Business Intelligence – entdecken Sie die vielfältigen Einsatzmöglichkeiten unserer B2B-Kontaktdatenbank.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={useCase.image} 
                  alt={useCase.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <useCase.icon className="w-6 h-6 text-[#2835F7]" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{useCase.description}</p>
                
                {/* Benefits */}
                <ul className="space-y-2">
                  {useCase.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-[#2835F7] rounded-full" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;